/**
 * PartType — frontend mirror of the NestJS PartType model (Phase 4a).
 *
 * PartType is a *catalog* row: it describes a kind of part
 * (e.g. "Hydraulic HP filter", "SAE 15W40 oil"). Each Part row
 * references one PartType. Like EquipmentType, this is a hybrid
 * catalog: global rows live in the ISI catalog and visible to all
 * companies; per-company custom rows are scoped to one company.
 *
 * Catalog scope:
 *   - ownerCompanyId === null  → global ISI catalog
 *   - ownerCompanyId === '...' → company-scoped custom type
 *
 * `requiresSerialTracking` drives whether individual part_serials
 * rows are expected (Phase 4c). True for critical rotating
 * machinery; false for bolts, filters, consumables.
 *
 * `defaultUnit` (default "piece") is inherited by Parts of this
 * type unless they override it. Examples: "L" for liquids, "kg"
 * for bulk materials.
 *
 * `defaultShelfLifeDays` is the expected shelf life — null means
 * unlimited. Drives part_batches expiration tracking.
 */

export const PART_TYPE_CATEGORIES = [
  'filter',
  'bearing',
  'seal',
  'gasket',
  'electrical',
  'hydraulic',
  'mechanical',
  'fluid',
  'consumable',
  'fastener',
  'other',
] as const;
export type PartTypeCategory = (typeof PART_TYPE_CATEGORIES)[number];

export interface PartType {
  id: string;
  name: string;
  code: string;
  category: PartTypeCategory;
  description: string | null;
  ownerCompanyId: string | null;
  requiresSerialTracking: boolean;
  defaultUnit: string;
  defaultShelfLifeDays: number | null;
  metadata: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** True when this type belongs to the global ISI catalog. */
export function isGlobalPartType(t: PartType): boolean {
  return t.ownerCompanyId === null;
}
